// Messages from the platform (admin console › Messages, 23/10/2026): planned
// maintenance, a new feature, a problem being fixed. The server decides who
// sees what (myMessages: the caller's own company and role, inside the
// message's window) — platform/** stays closed to every browser.
//   desktop: one slim banner at the top of the content column, with Dismiss;
//   phone:   a card on Today (the Today screens draw phoneMessageCards()).
// Text only, always: a message is drawn with textContent / esc, never as HTML.
// Dismissed ids are remembered in this browser only (a convenience).
// db.js is imported when first needed, so the pure parts load anywhere (tests).

const KEY = 'track.platformMessages.dismissed';
let current = [];

function dismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(KEY) || '[]')); } catch (_) { return new Set(); }
}
function dismiss(id) {
  const s = dismissed(); s.add(id);
  try { localStorage.setItem(KEY, JSON.stringify([...s].slice(-50))); } catch (_) { /* storage blocked */ }
}

const pad = (n) => String(n).padStart(2, '0');
/** 8pm, 8:30am */
const clock = (d, suffix = true) => `${d.getHours() % 12 || 12}${d.getMinutes() ? `:${pad(d.getMinutes())}` : ''}${suffix ? (d.getHours() < 12 ? 'am' : 'pm') : ''}`;

/**
 * PURE — the phone card's eyebrow from the window: "Tonight 8–9pm", "Tomorrow
 * 7am–12pm", "Thu 23 Oct 8–9pm", "Now until 9pm". Shown in capitals by the card.
 * Shared with the console's preview (admin/screens/messages.js).
 */
export function windowLabel(from, until, now = new Date()) {
  if (!from || from <= now) return until ? `Now until ${clock(until)}` : 'Now';
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const dayWord = sameDay(from, now) ? (from.getHours() >= 17 ? 'Tonight' : 'Today')
    : sameDay(from, tomorrow) ? 'Tomorrow'
    : from.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  if (!until || !sameDay(from, until)) return `${dayWord} from ${clock(from)}`;
  const samePart = (from.getHours() < 12) === (until.getHours() < 12);
  return `${dayWord} ${clock(from, !samePart)}–${clock(until)}`;
}
/** PURE — windowLabel from the server's ISO times. */
export function cardLabel(m, now = new Date()) {
  const d = (v) => { const x = v ? new Date(v) : null; return x && !Number.isNaN(x.getTime()) ? x : null; };
  return windowLabel(d(m && m.from), d(m && m.until), now);
}

/** PURE — the messages still to show: not dismissed, newest first, at most 2. */
export function toShow(list, gone = new Set()) {
  return (Array.isArray(list) ? list : []).filter((m) => m && m.id && m.text && !gone.has(m.id)).slice(0, 2);
}

const escText = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** The phone's Today cards (HTML; every value escaped). Empty string when there is nothing. */
export function phoneMessageCards(list = current) {
  return toShow(list, dismissed()).map((m) => `<div role="status" style="background:var(--m-card);border-radius:18px;box-shadow:var(--m-shadow-ring);padding:16px 16px 14px;display:flex;flex-direction:column;gap:6px${m.kind === 'problem' ? ';border-left:3px solid var(--m-attention)' : ''}">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px">
        <span style="font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:var(--m-ink2);font-weight:500">${escText(cardLabel(m))}</span>
        <span data-act="pm-dismiss:${escText(m.id)}" role="button" style="font-size:13.5px;color:var(--m-accent);min-height:32px;display:flex;align-items:center;cursor:pointer">Dismiss</span>
      </div>
      <div style="font-size:15px;line-height:1.4;white-space:pre-line;overflow-wrap:anywhere">${escText(m.text)}</div>
    </div>`).join('');
}

/** Fetch the caller's messages. Never throws: no messages is the fallback. */
export async function loadPlatformMessages() {
  try { const { callFunction } = await import('./db.js'); const r = await callFunction('myMessages', {}); current = (r && r.messages) || []; } catch (_) { current = []; }
  return current;
}

/** Desktop: draw (or remove) the banner at the top of the content column. */
export function drawDesktopBanner(list = current) {
  document.getElementById('pm-banner')?.remove();
  const [m] = toShow(list, dismissed());
  if (!m) return null;
  const main = document.querySelector('.main');
  if (!main) return null;
  const b = document.createElement('div');
  b.id = 'pm-banner';
  b.className = `pm-banner${m.kind === 'problem' ? ' problem' : ''}`;
  b.setAttribute('role', 'status');
  const dot = document.createElement('span'); dot.className = 'pm-dot'; dot.setAttribute('aria-hidden', 'true');
  const t = document.createElement('span'); t.className = 't'; t.textContent = m.text;
  const x = document.createElement('button'); x.type = 'button'; x.className = 'pm-x'; x.textContent = 'Dismiss';
  x.addEventListener('click', () => { dismiss(m.id); drawDesktopBanner(list); });
  b.append(dot, t, x);
  const seat = document.getElementById('seat-banner');
  main.insertBefore(b, seat ? seat.nextSibling : main.firstChild);
  return b;
}

/** Desktop start-up: load, draw, and look again every 15 minutes. */
export async function showPlatformMessages() {
  drawDesktopBanner(await loadPlatformMessages());
  setInterval(async () => drawDesktopBanner(await loadPlatformMessages()), 15 * 60000);
}

/** Phone start-up: load into the shell state (Today redraws), handle Dismiss. */
export async function startPhoneMessages({ setState, on }) {
  on('pm-dismiss', (id) => { dismiss(id); setState({ platformMessages: current.slice() }); });
  setState({ platformMessages: await loadPlatformMessages() });
}

export const _pm = { dismissed, dismiss, KEY };
